<template>
  <Teleport to="body">
    <div v-if="store.dialogMode.value" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/40" @click="close"></div>
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">{{ isSeoMode ? 'SEO Metadata' : 'Publish to CMS' }}</h2>
            <p v-if="isPublishMode && content?.cms_article_id" class="text-sm text-green-600 mt-0.5">
              Published (ID: {{ content.cms_article_id }})
              <span v-if="content.cms_pushed_at" class="text-gray-500">
                &middot; {{ formatDate(content.cms_pushed_at) }}
              </span>
            </p>
          </div>
          <button @click="close" class="text-gray-500 hover:text-gray-700">
            <X :size="20" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <!-- Metadata fields -->
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="text-sm font-medium text-gray-800 mb-1 block">Slug</label>
              <input v-if="isSeoMode" v-model="meta.slug" type="text" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
              <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.slug || '—' }}</p>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-800 mb-1 block">Excerpt</label>
              <textarea v-if="isSeoMode" v-model="meta.excerpt" rows="2" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
              <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 whitespace-pre-wrap">{{ meta.excerpt || '—' }}</p>
            </div>
            <div>
              <label class="text-sm font-medium text-gray-800 mb-1 block">Locale</label>
              <select v-if="isSeoMode" v-model="meta.locale" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500">
                <option value="sk">Slovak (sk)</option>
                <option value="cs">Czech (cs)</option>
                <option value="en">English (en)</option>
                <option value="de">German (de)</option>
              </select>
              <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.locale || '—' }}</p>
            </div>

            <!-- SEO fields (hidden for non-eligible formats) -->
            <template v-if="showSeoFields">
              <div class="border-t border-gray-200 pt-4">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="text-sm font-semibold text-gray-800">SEO & Geo Fields</h3>
                  <button
                    v-if="isSeoMode"
                    @click="autoGenerate"
                    :disabled="store.cmsMetadataGenerating.value"
                    class="text-xs px-2 py-1 rounded text-purple-600 hover:bg-purple-50 disabled:opacity-50 transition-colors"
                  >{{ store.cmsMetadataGenerating.value ? 'Generating...' : 'Regenerate' }}</button>
                </div>
                <div class="space-y-4">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="text-sm font-medium text-gray-800 mb-1 block">Meta Title <span class="text-gray-400 font-normal">(max 70)</span></label>
                      <template v-if="isSeoMode">
                        <input v-model="meta.metaTitle" type="text" maxlength="70" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
                        <p class="text-xs text-gray-400 mt-0.5">{{ (meta.metaTitle || '').length }}/70</p>
                      </template>
                      <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.metaTitle || '—' }}</p>
                    </div>
                    <div>
                      <label class="text-sm font-medium text-gray-800 mb-1 block">Keywords</label>
                      <input v-if="isSeoMode" v-model="meta.keywords" type="text" placeholder="seo, marketing, content" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
                      <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.keywords || '—' }}</p>
                    </div>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Meta Description <span class="text-gray-400 font-normal">(max 160)</span></label>
                    <template v-if="isSeoMode">
                      <textarea v-model="meta.metaDescription" rows="2" maxlength="160" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                      <p class="text-xs text-gray-400 mt-0.5">{{ (meta.metaDescription || '').length }}/160</p>
                    </template>
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 whitespace-pre-wrap">{{ meta.metaDescription || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Canonical URL</label>
                    <input v-if="isSeoMode" v-model="meta.canonicalUrl" type="text" placeholder="https://..." class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.canonicalUrl || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">OG Image URL</label>
                    <input v-if="isSeoMode" v-model="meta.ogImage" type="text" placeholder="https://..." class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.ogImage || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Quick Answer</label>
                    <textarea v-if="isSeoMode" v-model="meta.geoQuickAnswer" rows="2" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 whitespace-pre-wrap">{{ meta.geoQuickAnswer || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Key Takeaways <span class="text-gray-400 font-normal">(one per line)</span></label>
                    <textarea v-if="isSeoMode" v-model="geoKeyTakeawaysText" rows="4" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                    <ul v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 list-disc list-inside space-y-0.5">
                      <li v-for="(item, i) in (meta._geoKeyTakeaways || [])" :key="i">{{ item }}</li>
                      <li v-if="!(meta._geoKeyTakeaways || []).length" class="text-gray-400">—</li>
                    </ul>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">FAQ <span class="text-gray-400 font-normal">(Q: / A: format)</span></label>
                    <textarea v-if="isSeoMode" v-model="geoFaqText" rows="6" placeholder="Q: What is...?&#10;A: It is...&#10;&#10;Q: How does...?&#10;A: By..." class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                    <div v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 space-y-2">
                      <template v-for="(faq, i) in (meta._geoFaq || [])" :key="i">
                        <p><span class="font-medium">Q:</span> {{ faq.question }}</p>
                        <p><span class="font-medium">A:</span> {{ faq.answer }}</p>
                      </template>
                      <p v-if="!(meta._geoFaq || []).length" class="text-gray-400">—</p>
                    </div>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Sources</label>
                    <textarea v-if="isSeoMode" v-model="meta.geoSources" rows="2" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 whitespace-pre-wrap">{{ meta.geoSources || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Author Expertise</label>
                    <textarea v-if="isSeoMode" v-model="meta.geoAuthorExpertise" rows="2" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"></textarea>
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2 whitespace-pre-wrap">{{ meta.geoAuthorExpertise || '—' }}</p>
                  </div>
                  <div>
                    <label class="text-sm font-medium text-gray-800 mb-1 block">Last Reviewed</label>
                    <input v-if="isSeoMode" v-model="meta.geoLastReviewed" type="date" class="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500" />
                    <p v-else class="text-sm text-gray-900 bg-gray-50 rounded px-3 py-2">{{ meta.geoLastReviewed || '—' }}</p>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 shrink-0">
          <p v-if="isPublishMode && store.cmsPublishError.value" class="text-sm text-red-600 mb-2">{{ store.cmsPublishError.value }}</p>
          <p v-if="isSeoMode && saveError" class="text-sm text-red-600 mb-2">{{ saveError }}</p>
          <div class="flex justify-end gap-3">
            <button @click="close" class="text-sm px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
            <button
              v-if="isSeoMode"
              @click="save"
              :disabled="saving"
              class="text-sm px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >{{ saving ? 'Saving...' : 'Save' }}</button>
            <button
              v-if="isPublishMode"
              @click="publish"
              :disabled="store.cmsPublishing.value"
              class="text-sm px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >{{ store.cmsPublishing.value ? 'Publishing...' : (content?.cms_article_id ? 'Update CMS' : 'Publish') }}</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch, inject } from 'vue'
import { X } from 'lucide-vue-next'

const store = inject('workbench')

const NO_SEO_FORMATS = new Set([
  'social_post', 'linkedin_post', 'twitter_thread',
  'instagram_caption', 'email', 'brief_post'
])

const content = computed(() => store.currentContent.value)

const isSeoMode = computed(() => store.dialogMode.value === 'seo')
const isPublishMode = computed(() => store.dialogMode.value === 'publish')

const showSeoFields = computed(() => !NO_SEO_FORMATS.has(content.value?.format))

const saving = ref(false)
const saveError = ref(null)

const meta = reactive({
  slug: '',
  excerpt: '',
  metaTitle: '',
  metaDescription: '',
  keywords: '',
  locale: 'sk',
  canonicalUrl: '',
  ogImage: '',
  geoQuickAnswer: '',
  geoSources: '',
  geoAuthorExpertise: '',
  geoLastReviewed: '',
})

const geoKeyTakeawaysText = computed({
  get: () => (Array.isArray(meta._geoKeyTakeaways) ? meta._geoKeyTakeaways : []).join('\n'),
  set: (val) => { meta._geoKeyTakeaways = val.split('\n').filter(l => l.trim()) }
})

const geoFaqText = computed({
  get: () => {
    const items = meta._geoFaq || []
    return items.map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n')
  },
  set: (val) => {
    const items = []
    const blocks = val.split(/\n\n+/)
    for (const block of blocks) {
      const qMatch = block.match(/^Q:\s*(.+)/m)
      const aMatch = block.match(/^A:\s*(.+)/m)
      if (qMatch && aMatch) {
        items.push({ question: qMatch[1].trim(), answer: aMatch[1].trim() })
      }
    }
    meta._geoFaq = items
  }
})

function syncMetaFromContent() {
  const m = content.value?.cms_metadata
  if (m) {
    meta.slug = m.slug || ''
    meta.excerpt = m.excerpt || ''
    meta.metaTitle = m.metaTitle || ''
    meta.metaDescription = m.metaDescription || ''
    meta.keywords = m.keywords || ''
    meta.locale = m.locale || 'sk'
    meta.canonicalUrl = m.canonicalUrl || ''
    meta.ogImage = m.ogImage || ''
    meta.geoQuickAnswer = m.geoQuickAnswer || ''
    meta._geoKeyTakeaways = m.geoKeyTakeaways || []
    meta._geoFaq = m.geoFaq || []
    meta.geoSources = m.geoSources || ''
    meta.geoAuthorExpertise = m.geoAuthorExpertise || ''
    meta.geoLastReviewed = m.geoLastReviewed || ''
  }
}

// Sync metadata when dialog opens
watch(() => store.dialogMode.value, (mode) => {
  if (mode) {
    saveError.value = null
    syncMetaFromContent()
    // Auto-generate if opening SEO mode with no metadata
    if (mode === 'seo' && !content.value?.cms_metadata?.metaTitle) {
      autoGenerate()
    }
  }
})

// Also sync when metadata is regenerated
watch(() => content.value?.cms_metadata, (m) => {
  if (m && store.dialogMode.value) {
    meta.slug = m.slug || meta.slug
    meta.excerpt = m.excerpt || meta.excerpt
    meta.metaTitle = m.metaTitle || meta.metaTitle
    meta.metaDescription = m.metaDescription || meta.metaDescription
    meta.keywords = m.keywords || meta.keywords
    meta.locale = m.locale || meta.locale
    meta.geoQuickAnswer = m.geoQuickAnswer || meta.geoQuickAnswer
    if (m.geoKeyTakeaways) meta._geoKeyTakeaways = m.geoKeyTakeaways
    if (m.geoFaq) meta._geoFaq = m.geoFaq
  }
}, { deep: true })

function close() {
  store.dialogMode.value = null
  store.cmsPublishError.value = null
  saveError.value = null
}

async function autoGenerate() {
  try {
    await store.generateCmsMetadata()
  } catch (e) {
    // Error shown via store state
  }
}

function buildMetadata() {
  return {
    slug: meta.slug,
    excerpt: meta.excerpt,
    metaTitle: meta.metaTitle,
    metaDescription: meta.metaDescription,
    keywords: meta.keywords,
    locale: meta.locale,
    canonicalUrl: meta.canonicalUrl,
    ogImage: meta.ogImage,
    geoQuickAnswer: meta.geoQuickAnswer,
    geoKeyTakeaways: meta._geoKeyTakeaways || [],
    geoFaq: meta._geoFaq || [],
    geoSources: meta.geoSources,
    geoAuthorExpertise: meta.geoAuthorExpertise,
    geoLastReviewed: meta.geoLastReviewed,
  }
}

async function save() {
  saving.value = true
  saveError.value = null
  try {
    await store.saveCmsMetadata(buildMetadata())
    close()
  } catch (e) {
    saveError.value = e.message || 'Failed to save metadata'
  } finally {
    saving.value = false
  }
}

async function publish() {
  try {
    await store.pushToCms(buildMetadata())
    close()
  } catch (e) {
    // Error displayed in footer
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString()
}
</script>
